import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { format } from 'date-fns';
import {
  PageTitle, KPI, KPIStrip,
  Badge, Button, Card,
  Table, THead, TBody, TR, TH, Row, Cell,
  EmptyState,
  Icons,
} from '../shared/ui';

// ── constants ─────────────────────────────────────────────────────────────────
const EVENT_TONE = {
  stranger:                'danger',
  vehicle_detected:        'info',
  fire_escape_obstruction: 'warning',
  curfew_violation:        'warning',
  lpr_mismatch:            'violet',
};

const formatEventType = (type) =>
  type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

// ── sub-components ────────────────────────────────────────────────────────────
const EventCard = ({ event, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:border-stone-300 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group"
  >
    {event.image_url && (
      <img src={event.image_url} alt={`Event ${event.id}`} className="w-full h-32 object-cover" />
    )}
    <div className="p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icons.Camera size={14} className="text-stone-400" />
          <span className="text-sm font-medium text-stone-900">{formatEventType(event.type)}</span>
        </div>
        {event.is_resolved
          ? <Icons.CheckCircle2 size={16} className="text-green-500" />
          : <Icons.Alert size={16} className="text-red-500" />
        }
      </div>
      <p className="text-sm text-stone-600">{event.location}</p>
      <p className="text-xs text-stone-400">{format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm:ss')}</p>
    </div>
  </div>
);

const EventDetailsModal = ({ event, onClose, onResolve }) => (
  <div className="fixed inset-0 bg-stone-900/50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl border border-stone-200 shadow-popover max-w-2xl w-full">
      {/* header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
        <div>
          <h2 className="text-base font-semibold text-stone-900">Event Details</h2>
          <p className="text-xs text-stone-500">ID: #{event.id}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
        >
          <Icons.X size={16} />
        </button>
      </div>

      <div className="p-5 max-h-[80vh] overflow-y-auto space-y-5">
        {event.image_url && (
          <div className="rounded-lg overflow-hidden border border-stone-200">
            <img src={event.image_url} alt={`Event ${event.id}`} className="w-full h-auto object-contain bg-stone-50" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 bg-stone-50 rounded-lg p-4 border border-stone-100">
          <div>
            <p className="text-2xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Event Type</p>
            <p className="text-sm font-medium text-stone-900">{formatEventType(event.type)}</p>
          </div>
          <div>
            <p className="text-2xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Status</p>
            <Badge tone={event.is_resolved ? 'success' : 'danger'}>
              {event.is_resolved ? 'Resolved' : 'Pending'}
            </Badge>
          </div>
          <div>
            <p className="text-2xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Location</p>
            <p className="text-sm font-medium text-stone-900">{event.location}</p>
          </div>
          <div>
            <p className="text-2xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Timestamp</p>
            <p className="text-sm font-medium text-stone-900">{format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm:ss')}</p>
          </div>
        </div>

        {event.details && Object.keys(event.details).length > 0 && (
          <div>
            <p className="text-sm font-semibold text-stone-900 mb-2">Additional Analysis</p>
            <div className="bg-stone-900 text-stone-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
              <pre>{JSON.stringify(event.details, null, 2)}</pre>
            </div>
          </div>
        )}

        {!event.is_resolved && (
          <div className="pt-4 border-t border-stone-100">
            <Button kind="primary" className="w-full justify-center" iconLeft={<Icons.CheckCircle2 size={16} />} onClick={onResolve}>
              Mark as Resolved
            </Button>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ── main component ────────────────────────────────────────────────────────────
const CVMonitoring = () => {
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter]       = useState('all');
  const [selectedEvent, setSelectedEvent]     = useState(null);
  const [viewMode, setViewMode]               = useState('list');

  const { data: events = [], isLoading, refetch } = useQuery({
    queryKey: ['cv-events', eventTypeFilter, statusFilter],
    queryFn: async () => {
      const r = await api.get('/api/cv-events', {
        params: {
          type:        eventTypeFilter !== 'all' ? eventTypeFilter : undefined,
          is_resolved: statusFilter === 'resolved' ? true : statusFilter === 'unresolved' ? false : undefined,
        },
      });
      return r.data;
    },
    refetchInterval: 30000,
    select: (data) => Array.isArray(data) ? data : [],
  });

  const stats = {
    total:    events.length,
    unresolved: events.filter((e) => !e.is_resolved).length,
    today:    events.filter((e) => new Date(e.timestamp).toDateString() === new Date().toDateString()).length,
    critical: events.filter((e) => ['stranger', 'fire_escape_obstruction', 'curfew_violation'].includes(e.type)).length,
  };

  const SELECT_CLS = 'h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600';

  return (
    <div className="space-y-6">
      <PageTitle
        title="CV Monitoring"
        subtitle="Real-time AI security surveillance and alerts"
        actions={
          <Button kind="secondary" size="sm" iconLeft={<Icons.Refresh size={14} />} onClick={() => refetch()}>
            Refresh
          </Button>
        }
      />

      <KPIStrip>
        <KPI label="Total Events"    value={stats.total}      icon={<Icons.Camera size={14} />}  accent="info"    />
        <KPI label="Unresolved"      value={stats.unresolved} icon={<Icons.Alert size={14} />}   accent="danger"  />
        <KPI label="Today's Events"  value={stats.today}      icon={<Icons.Clock size={14} />}   accent="warning" />
        <KPI label="Critical Events" value={stats.critical}   icon={<Icons.Flag size={14} />}    accent="neutral" />
      </KPIStrip>

      {/* Filters */}
      <Card padding={16}>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)} className={SELECT_CLS}>
            <option value="all">All Event Types</option>
            <option value="stranger">Stranger Detection</option>
            <option value="vehicle_detected">Vehicle Detection</option>
            <option value="fire_escape_obstruction">Fire Escape Obstruction</option>
            <option value="curfew_violation">Curfew Violation</option>
            <option value="lpr_mismatch">License Plate Mismatch</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={SELECT_CLS}>
            <option value="all">All Status</option>
            <option value="unresolved">Unresolved</option>
            <option value="resolved">Resolved</option>
          </select>

          {/* View toggle */}
          <div className="flex items-center gap-1 p-1 bg-stone-100 rounded-lg ml-auto border border-stone-200">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-teal-600 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
            >
              <Icons.Menu size={16} />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white text-teal-600 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
            >
              <Icons.Layers size={16} />
            </button>
          </div>
        </div>
      </Card>

      {/* Events display */}
      {isLoading ? (
        <Card padding={32}>
          <div className="text-center py-4">
            <Icons.Spinner size={32} className="text-teal-600 mx-auto mb-3 animate-spin" />
            <p className="text-stone-500 text-sm">Loading CV events…</p>
          </div>
        </Card>
      ) : events.length === 0 ? (
        <Card padding={32}>
          <EmptyState
            icon={<Icons.Camera size={32} />}
            title="No CV events found"
            description="Events will appear here as they are detected"
          />
        </Card>
      ) : viewMode === 'list' ? (
        <Table>
          <THead>
            <TR>
              <TH>Time</TH>
              <TH>Event Type</TH>
              <TH>Location</TH>
              <TH>Details</TH>
              <TH>Status</TH>
              <TH align="right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {events.map((event) => (
              <Row key={event.id}>
                <Cell>
                  <div className="text-sm">
                    <p className="font-medium text-stone-900">{format(new Date(event.timestamp), 'MMM dd')}</p>
                    <p className="text-stone-500">{format(new Date(event.timestamp), 'HH:mm:ss')}</p>
                  </div>
                </Cell>
                <Cell>
                  <div className="flex items-center gap-2">
                    <Icons.Camera size={14} className="text-stone-400 shrink-0" />
                    <Badge tone={EVENT_TONE[event.type] || 'neutral'}>
                      {formatEventType(event.type)}
                    </Badge>
                  </div>
                </Cell>
                <Cell className="text-stone-700 text-sm">{event.location}</Cell>
                <Cell className="text-stone-600 text-sm">
                  {event.details?.license_plate && `Plate: ${event.details.license_plate}`}
                  {event.details?.person_count && `${event.details.person_count} person(s)`}
                  {event.details?.vehicle_type && `Type: ${event.details.vehicle_type}`}
                </Cell>
                <Cell>
                  <Badge tone={event.is_resolved ? 'success' : 'danger'}>
                    {event.is_resolved ? 'Resolved' : 'Pending'}
                  </Badge>
                </Cell>
                <Cell align="right">
                  <button
                    onClick={() => setSelectedEvent(event)}
                    className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
                  >
                    View
                  </button>
                </Cell>
              </Row>
            ))}
          </TBody>
        </Table>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <EventCard key={event.id} event={event} onClick={() => setSelectedEvent(event)} />
          ))}
        </div>
      )}

      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onResolve={() => { refetch(); setSelectedEvent(null); }}
        />
      )}
    </div>
  );
};

export default CVMonitoring;
